import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { RotateCcw } from 'lucide-react';
import { Driver, Forecast, forecastsApi } from '@/lib/forecasts';
import { TotalsByPeriodChart } from '@/components/charts/TotalsByPeriodChart';

/** Default horizon: the forecast's stored horizon, else the next 12 months. */
function defaultHorizon(fc: Forecast): { start: string; end: string } {
  if (fc.horizonStart && fc.horizonEnd) return { start: fc.horizonStart, end: fc.horizonEnd };
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 12, 0);
  const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return { start: iso(start), end: iso(end) };
}

const fmt = (n: number) => n.toLocaleString(undefined, { maximumFractionDigits: 0 });

/**
 * What-if scenario sliders (NUMBER_ONE_PLAN W3-1). Adjust each driver's value
 * and see the projection recompute live against the saved baseline — no facts
 * are written (the backend `simulate` reuses the exact compute math). The
 * headline shows the scenario total vs baseline and the delta.
 */
export function WhatIfPanel({ forecast }: { forecast: Forecast }) {
  const { t } = useTranslation();
  const h = defaultHorizon(forecast);
  const [drivers, setDrivers] = useState<Driver[]>(() => forecast.drivers.map((d) => ({ ...d })));
  const [horizonStart] = useState(h.start);
  const [horizonEnd] = useState(h.end);
  const [showBands, setShowBands] = useState(false);

  // Debounce slider drags before hitting the API.
  const sig = JSON.stringify({ drivers, horizonStart, horizonEnd });
  const [debSig, setDebSig] = useState(sig);
  useEffect(() => {
    const id = setTimeout(() => setDebSig(sig), 300);
    return () => clearTimeout(id);
  }, [sig]);

  const { data, isFetching } = useQuery({
    queryKey: ['simulate', forecast.id, debSig],
    queryFn: () => forecastsApi.simulate(forecast.id, { horizonStart, horizonEnd, drivers }),
    retry: false,
    staleTime: 60_000,
  });

  const series = useMemo(() => {
    if (!data) return [];
    const s = [
      { name: t('whatif.baseline', { defaultValue: 'Baseline' }), data: data.periods.map((x, i) => ({ x, y: data.baseline[i] })) },
      { name: t('whatif.scenario', { defaultValue: 'What-if' }), data: data.periods.map((x, i) => ({ x, y: data.scenario[i] })) },
    ];
    if (showBands && data.sigma > 0) {
      s.push(
        { name: t('whatif.p90', { defaultValue: 'P90 (optimistic)' }), data: data.periods.map((x, i) => ({ x, y: data.p90[i] })) },
        { name: t('whatif.p10', { defaultValue: 'P10 (pessimistic)' }), data: data.periods.map((x, i) => ({ x, y: data.p10[i] })) },
      );
    }
    return s;
  }, [data, t, showBands]);

  const baseTotal = data ? data.baseline.reduce((s, v) => s + v, 0) : 0;
  const scenTotal = data ? data.scenario.reduce((s, v) => s + v, 0) : 0;
  const delta = scenTotal - baseTotal;
  const deltaPct = baseTotal !== 0 ? (delta / Math.abs(baseTotal)) * 100 : null;

  const reset = () => setDrivers(forecast.drivers.map((d) => ({ ...d })));
  const setValue = (i: number, value: number) =>
    setDrivers((ds) => ds.map((d, j) => (j === i ? { ...d, value } : d)));

  // Per-driver slider bounds: 0 … 2× the saved value (min span so flat-0 still moves).
  const bound = (orig: number) => Math.max(Math.abs(orig) * 2, 1000);

  return (
    <div className="mt-3 rounded-xl border border-emerald-200 bg-emerald-50/40 p-4">
      <div className="flex items-center justify-between mb-2 gap-3 flex-wrap">
        <h4 className="text-sm font-semibold text-ink-800">{t('whatif.title', { defaultValue: 'What-if' })}</h4>
        <div className="flex items-center gap-3">
          <label className="inline-flex items-center gap-1.5 text-[11px] text-ink-600 cursor-pointer">
            <input type="checkbox" checked={showBands} onChange={(e) => setShowBands(e.target.checked)} className="accent-emerald-600" />
            {t('whatif.show_bands', { defaultValue: 'Show confidence range' })}
          </label>
          <button onClick={reset} className="inline-flex items-center gap-1 text-[11px] text-ink-600 hover:text-foreground">
            <RotateCcw className="h-3 w-3" /> {t('whatif.reset', { defaultValue: 'Reset to baseline' })}
          </button>
        </div>
      </div>

      {/* Headline */}
      <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1 mb-3">
        <div>
          <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{t('whatif.scenario_total', { defaultValue: 'What-if total' })}</span>
          <div className="num text-xl font-semibold text-ink-900 tabular-nums">{fmt(scenTotal)}</div>
        </div>
        <div>
          <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{t('whatif.vs_baseline', { defaultValue: 'vs baseline' })}</span>
          <div className={`num text-base font-semibold tabular-nums ${delta >= 0 ? 'text-emerald-700' : 'text-rose-600'}`}>
            {delta >= 0 ? '+' : ''}{fmt(delta)}{deltaPct !== null ? ` (${delta >= 0 ? '+' : ''}${deltaPct.toFixed(1)}%)` : ''}
          </div>
        </div>
        {showBands && data && data.sigma > 0 && (
          <div>
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{t('whatif.range', { defaultValue: 'P10–P90 range' })}</span>
            <div className="num text-base font-semibold text-ink-700 tabular-nums">
              {fmt(data.p10.reduce((s, v) => s + v, 0))} – {fmt(data.p90.reduce((s, v) => s + v, 0))}
            </div>
          </div>
        )}
        {isFetching && <span className="text-[11px] text-muted-foreground">{t('whatif.recomputing', { defaultValue: 'recomputing…' })}</span>}
      </div>

      {series.length > 0 && (
        <div className="rounded-md border border-border bg-white p-3 mb-3">
          <TotalsByPeriodChart series={series} />
        </div>
      )}

      {/* Driver sliders */}
      <div className="space-y-2.5">
        {drivers.map((d, i) => {
          const orig = forecast.drivers[i]?.value ?? 0;
          const max = bound(orig);
          return (
            <div key={`${d.name}-${i}`} className="grid grid-cols-[1fr_auto] items-center gap-3">
              <div className="min-w-0">
                <div className="flex items-center justify-between">
                  <span className="text-[12.5px] font-medium text-ink-800 truncate">{d.name || t('whatif.unnamed', { defaultValue: 'Driver' })}</span>
                  <span className="text-[10px] text-muted-foreground ml-2 shrink-0">{d.basis}</span>
                </div>
                <input
                  type="range"
                  min={0}
                  max={Math.round(max)}
                  step={Math.max(1, Math.round(max / 200))}
                  value={Math.min(Math.max(d.value, 0), Math.round(max))}
                  onChange={(e) => setValue(i, Number(e.target.value))}
                  className="w-full accent-emerald-600"
                  aria-label={d.name}
                />
              </div>
              <input
                type="number"
                value={d.value}
                onChange={(e) => setValue(i, Number(e.target.value))}
                className="num w-28 rounded-md border border-border px-2 py-1 text-sm text-right tabular-nums"
              />
            </div>
          );
        })}
        {drivers.length === 0 && (
          <p className="text-[12px] text-muted-foreground">{t('whatif.no_drivers', { defaultValue: 'This scenario has no adjustable drivers yet.' })}</p>
        )}
      </div>
      <p className="mt-2 text-[11px] text-muted-foreground">
        {t('whatif.note', { defaultValue: 'Live preview — nothing is saved. History-based drivers project from your actuals and are unaffected by the slider.' })}
      </p>
    </div>
  );
}
